/**
 * Loading State Component
 *
 * Displays a loading spinner with optional message.
 * Fixes issue #1253: Code Duplication - Loading State Component Pattern
 */

import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingStateProps {
  /**
   * Loading message to display
   */
  message?: string;

  /**
   * Size of the spinner
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether to display fullscreen (fixed position)
   */
  fullScreen?: boolean;

  /**
   * Custom className for styling
   */
  className?: string;

  /**
   * Custom loading indicator component
   */
  spinner?: React.ReactNode;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  message = 'Loading...',
  size = 'md',
  fullScreen = false,
  className,
  spinner
}) => {
  const sizeClasses = {
    sm: { spinner: 'w-4 h-4', text: 'text-sm' },
    md: { spinner: 'w-8 h-8', text: 'text-base' },
    lg: { spinner: 'w-12 h-12', text: 'text-lg' }
  };

  const sizeConfig = sizeClasses[size];

  const containerClass = fullScreen
    ? 'fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-[var(--z-modal)]'
    : 'flex items-center justify-center p-8';

  return (
    <div
      className={`${containerClass} ${className || ''}`}
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      <div className="flex flex-col items-center gap-3">
        {spinner || (
          <Loader2 className={`${sizeConfig.spinner} animate-spin text-blue-500`} />
        )}
        {message && (
          <p className={`text-gray-600 ${sizeConfig.text}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Preset loading states for common scenarios
 */

export const LoadingCarriers: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => (
  <LoadingState
    message="Loading carriers..."
    size={size}
  />
);

export const LoadingVehicles: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => (
  <LoadingState
    message="Loading vehicles..."
    size={size}
  />
);

export const LoadingDrivers: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => (
  <LoadingState
    message="Loading drivers..."
    size={size}
  />
);

export const LoadingData: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => (
  <LoadingState
    message="Loading data..."
    size={size}
  />
);

export const LoadingSpinnerOnly: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => (
  <LoadingState
    message={undefined}
    size={size}
  />
);
